/**
 * AdminCCTV.jsx
 * 
 * Camera Registry - Security Operations Centre View
 * Phase 1: Static placeholder grid with location-based naming
 * 
 * NO backend calls
 * NO API integration
 * NO authentication logic
 * NO network requests
 */

import { Video, Maximize2, AlertCircle } from "lucide-react";

// Camera Registry - Location-first naming (static placeholders)
const CAMERA_LOCATIONS = [
  { id: "cam_01", location: "Main Entrance", zone: "Access" },
  { id: "cam_02", location: "East Entrance", zone: "Access" },
  { id: "cam_03", location: "West Entrance", zone: "Access" },
  { id: "cam_04", location: "North Mall", zone: "Retail" },
  { id: "cam_05", location: "South Mall", zone: "Retail" },
  { id: "cam_06", location: "Central Mall", zone: "Retail" },
  { id: "cam_07", location: "Service Yard", zone: "Operations" },
  { id: "cam_08", location: "Loading Area", zone: "Operations" },
  { id: "cam_09", location: "Office Entrance", zone: "Office" },
  { id: "cam_10", location: "Office Reception", zone: "Office" },
  { id: "cam_11", location: "Car Park Access", zone: "Parking" },
  { id: "cam_12", location: "Car Park Overview", zone: "Parking" },
  { id: "cam_13", location: "Stairwell", zone: "Internal" },
  { id: "cam_14", location: "Lift Lobby", zone: "Internal" },
  { id: "cam_15", location: "Retail Corridor", zone: "Retail" },
  { id: "cam_16", location: "External Perimeter", zone: "Security" },
];

const ZONE_COLORS = {
  Access: "from-blue-500/20 to-blue-600/10",
  Retail: "from-emerald-500/20 to-emerald-600/10",
  Operations: "from-amber-500/20 to-amber-600/10",
  Office: "from-purple-500/20 to-purple-600/10",
  Parking: "from-cyan-500/20 to-cyan-600/10",
  Internal: "from-slate-500/20 to-slate-600/10",
  Security: "from-red-500/20 to-red-600/10",
};

const ZONE_BADGE_COLORS = {
  Access: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  Retail: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  Operations: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  Office: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  Parking: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  Internal: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  Security: "bg-red-500/20 text-red-400 border-red-500/30",
};

export default function AdminCCTV() {
  return (
    <div className="min-h-screen bg-slate-950 p-6" data-testid="admin-cctv">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 
              className="text-2xl font-bold text-white mb-1" 
              style={{ fontFamily: 'Outfit, sans-serif' }} 
              data-testid="cctv-title"
            >
              CCTV
            </h1>
            <p className="text-slate-400 text-sm">
              Kyle Centre Camera Registry — Phase 1
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-lg border border-slate-700">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-xs text-slate-400">Placeholders Only</span>
          </div>
        </div>
      </div>

      {/* 4x4 Camera Grid */}
      <div className="grid grid-cols-4 gap-4" data-testid="cctv-grid">
        {CAMERA_LOCATIONS.map((camera, index) => (
          <div
            key={camera.id}
            className={`
              relative aspect-video rounded-lg overflow-hidden
              bg-gradient-to-br ${ZONE_COLORS[camera.zone]}
              border border-slate-700/50
              hover:border-slate-600 hover:scale-[1.02]
              transition-all duration-200 cursor-pointer
              group
            `}
            data-testid={`camera-tile-${index + 1}`}
          >
            {/* Placeholder Content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
              <div className="w-12 h-12 rounded-full bg-slate-800/80 flex items-center justify-center mb-3 group-hover:bg-slate-700/80 transition-colors">
                <Video className="w-6 h-6 text-slate-500 group-hover:text-slate-400 transition-colors" />
              </div>
              <h3 className="text-white font-medium text-sm text-center mb-1">
                {camera.location}
              </h3>
              <span className={`text-xs px-2 py-0.5 rounded-full border ${ZONE_BADGE_COLORS[camera.zone]}`}>
                {camera.zone}
              </span>
            </div>

            {/* Status Indicator */}
            <div className="absolute top-3 left-3 flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-slate-600" />
              <span className="text-xs text-slate-500">Offline</span>
            </div>

            {/* Expand Button (Hover) */}
            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <button 
                className="w-7 h-7 rounded bg-slate-800/90 flex items-center justify-center hover:bg-slate-700 transition-colors"
                title="Expand"
              >
                <Maximize2 className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </div>

            {/* Camera ID */}
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
              <span className="text-xs text-slate-600 font-mono">{camera.id}</span>
            </div>

            {/* Scan Lines Effect */}
            <div 
              className="absolute inset-0 pointer-events-none opacity-[0.03]"
              style={{
                backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
              }}
            />
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-6 flex items-center justify-between text-xs text-slate-600">
        <div className="flex items-center gap-4">
          <span>16 Cameras Registered</span>
          <span className="w-1 h-1 rounded-full bg-slate-700" />
          <span>0 Online</span>
          <span className="w-1 h-1 rounded-full bg-slate-700" />
          <span>Phase 1 — Static Placeholders</span>
        </div>
        <div className="flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>Streaming integration pending</span>
        </div>
      </div>
    </div>
  );
}
